/* Copyright (C) mroads, LLC - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Written by Premsagar Choudhary <premsagar.choudhary@mroads.com>, June 2023
 * @format
 * @flow
 */

import React from 'react';
import { useSelector } from 'react-redux';
import { SketchPicker } from 'react-color';
import { Box, FormControl, InputAdornment, Typography, Input, Grid, IconButton } from '@material-ui/core';
import { DeleteOutline as DeleteIcon } from '@material-ui/icons';
import useStyles from './useStyles';
import FireboardUtils from '../../../../../util/fireboard-util';
import TableOrientation from '../ToolsWrapper/TableOrientation';
import constants from '../../../constants';
import { shapeIdEnum } from '../../../../../enums';
import Footer from '../Footer';

const Properties = ({ selectedShape, onChangeShape, onDeleteShape, onSave, onClose, }) => {
  const [shapeType, setShapeType] = React.useState(null);
  const { labelsData } = useSelector(state => state);
  const labels = labelsData?.labels;
  const classes = useStyles();

  const shape = selectedShape || {};
  const { seatCapacity, shapeId, stationId, stationNumber } = shape;
  const shapeTypeArray = seatCapacity > 0 ? constants.tableVariant[seatCapacity] : [];

  React.useEffect(() => {
    const initialShapeType = shapeTypeArray.find(it => it.value === shapeId);
    setShapeType(initialShapeType);
  }, [shapeId, shapeTypeArray]);

  const onPropertyChange = (value, property) => {
    if (!FireboardUtils.isObjectBlank(selectedShape)) {
      onChangeShape && onChangeShape({
        ...shape,
        [property]: value,
      });
    }
  };

  const onChooseOrientation = (orientation) => {
    setShapeType(orientation);
    onPropertyChange(+orientation?.value, 'shapeId');
  };

  const getShapeName = () => {
    let name = '';
    if (shapeId === shapeIdEnum.RECTANGLE)
      name = labels.DESIGNER_RECTANGLE;
    else if (shapeId === shapeIdEnum.SQUARE)
      name = labels.DESIGNER_SQUARE;
    else if (shapeId === shapeIdEnum.CIRCLE)
      name = labels.DESIGNER_CIRCLE;
    else if (shapeId === shapeIdEnum.POLYGON)
      name = labels.DESIGNER_POLYGON;
    else if (shapeId === shapeIdEnum.DOOR_IMG)
      name = labels.DESIGNER_DOOR;
    else if (shapeId === shapeIdEnum.WINDOW_IMG)
      name = labels.DESIGNER_WINDOW;
    else if (shapeId === shapeIdEnum.AQUARIUM_IMG)
      name = labels.DESIGNER_AQUARIUM;
    else if (shapeId === shapeIdEnum.PAINTING_IMG)
      name = labels.DESIGNER_PAINTING;
    else if (shapeId === shapeIdEnum.RESTROOM_IMG)
      name = labels.DESIGNER_RESTROOM;
    else if (shapeId === shapeIdEnum.KITCHEN_IMG)
      name = labels.DESIGNER_KITCHEN;
    return name;
  };

  const isTable = () => {
    return (stationId !== null && stationId !== undefined);
  };

  return (
    <React.Fragment>
      <Box display={'flex'} className={classes.propertyNameWrapper} p={1} borderRadius={'6px'}>
        <Box width={'50%'} display={'flex'} alignItems={'center'} justifyContent={'flex-start'}>
          <Typography variant='body1' className={classes.shapeTitle}>
            {isTable() ? `${labels.DESIGNER_TABLE} ${stationNumber}` : getShapeName()}
          </Typography>
        </Box>
        <Box width={'50%'} display={'flex'} alignItems={'center'} justifyContent={'flex-end'}>
          <IconButton className={classes.actionBtn} onClick={onDeleteShape}>
            <DeleteIcon className={classes.actionIcon} />
          </IconButton>
        </Box>
      </Box>
      <ColoPicker
        shape={shape}
        onChange={onPropertyChange}
      />
      <InputProperty
        shape={shape}
        selectedShape={selectedShape}
        onChange={onPropertyChange}
      />
      {isTable() && (
        <TableOrientation
          tableOrientation={shapeTypeArray}
          onChooseOrientation={onChooseOrientation}
          selectedTableShape={shapeType}
        />
      )}
      <Footer
        onCancel={onClose}
        onOk={onSave}
      />
    </React.Fragment>
  );
};

export default Properties;

const InputProperty = ({ shape, onChange }) => {
  const { labelsData } = useSelector(state => state);
  const labels = labelsData?.labels;
  const classes = useStyles();

  const PropertyArray = [
    {
      name: labels.DESIGNER_SIZE,
      props: [
        { key: 'width', inputAdornment: 'W' },
        { key: 'height', inputAdornment: 'H' },
      ],
    },
    {
      name: labels.DESIGNER_POSITION,
      props: [
        { key: 'x', inputAdornment: 'X' },
        { key: 'y', inputAdornment: 'Y' },
      ],
    },
    {
      name: labels.DESIGNER_SCALE,
      props: [
        { key: 'scaleX', inputAdornment: 'X' },
        { key: 'scaleY', inputAdornment: 'Y' },
      ],
    },
    {
      name: labels.DESIGNER_ANGLE,
      props: [
        { key: 'rotation', inputAdornment: '∠' },
      ],
    },
    {
      name: labels.DESIGNER_STROKE_WIDTH,
      props: [
        { key: 'strokeWidth', inputAdornment: '=' },
      ],
    },
  ];

  const getType = key => {
    switch (key) {
      case 'x':
      case 'y':
      case 'width':
      case 'height':
      case 'strokeWidth':
      case 'rotation':
      case 'scaleX':
      case 'scaleY':
        return 'number';
      default: return 'text';
    }
  };

  const onInputChange = (e, property) => {
    const newValue = e.target.value;
    let value = getType(property) === 'number' ? +newValue : newValue;
    onChange && onChange(value, property);
  };

  const formatValue = (value, property) => {
    if (value === undefined || value === null || value === '')
      return '';
    if (getType(property) === 'number') {
      const arr = String(value).split('.');
      let val = arr && arr.length && arr[0];
      if (arr && arr.length > 1) {
        val = `${arr[0]}.${arr[1].slice(0, 2)}`;
      }
      return +val;
    }
    return value;
  };

  const getValue = property => {
    let value = '';
    if (shape && Object.keys(shape).length != 0 && shape.constructor === Object) {
      value = shape[property];
    }
    return formatValue(value, property);
  };

  const isDisabled = eachProperty => {
    //disable rotation for all the tables
    if (!FireboardUtils.isObjectBlank(shape) && shape?.stationId && eachProperty === 'rotation') {
      return true;
    }
    return !([eachProperty] in shape);
  };

  return (
    <Box className={classes.root}>
      {PropertyArray.map(eachProprty =>
        <Box mb={1} key={eachProprty.name}>
          <Typography variant='body1'>{eachProprty.name}</Typography>
          <Box display={'flex'} mt={0.5}>
            {eachProprty.props.map(eachProp => (
              <Box width={'50%'} key={eachProp.key}>
                <FormControl className={classes.formControl}>
                  <Input
                    id="size-width"
                    type={getType(eachProp.key)}
                    value={getValue(eachProp.key)}
                    onChange={e => onInputChange(e, eachProp.key)}
                    disabled={isDisabled(eachProp.key)}
                    startAdornment={<InputAdornment position="start">{eachProp.inputAdornment}</InputAdornment>}
                    disableUnderline
                    className={classes.input}
                    classes={{
                      input: classes.input
                    }}
                  />
                </FormControl>
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

const ColoPicker = ({ shape, onChange }) => {
  const classes = useStyles();
  const [colorPicker, setColorPicker] = React.useState({
    fill: false,
    stroke: false,
  });
  const [colors, setColors] = React.useState({
    fill: '',
    stroke: '',
  });
  const { labelsData } = useSelector(state => state);
  const labels = labelsData?.labels;

  const ColorProperty = [{ key: 'fill', value: labels.DESIGNER_FILL }, { key: 'stroke', value: labels.DESIGNER_STROKE }];

  React.useEffect(() => {
    setColors({
      fill: shape?.fill || '',
      stroke: shape?.stroke || '',
    });
  }, [shape]);

  const handleColorChange = (color, property) => {
    setColors(prevState => ({
      ...prevState,
      [property]: color,
    }));
    onChange && onChange(color, property);
  };

  const isDisabled = () => {
    if (!FireboardUtils.isObjectBlank(shape)) {
      if (shape?.stationId) //can't change stroke & fill color for tables
        return true;
      return false; //we can change fill & stroke color for others shape
    }
    return true;
  };

  const visibleColorPicker = key => {
    if (!isDisabled()) {
      let defaultState = {
        fill: false,
        stroke: false,
      };
      setColorPicker({
        ...defaultState,
        [key]: true,
      });
    }
  };

  const closeColorPicker = () => {
    setColorPicker({
      fill: false,
      stroke: false,
    });
  };

  return (
    <Box p={1}>
      <Typography variant='body1' className={classes.fillTitle}>{labels.DESIGNER_FILL_STROKE}</Typography>
      <Grid container>
        {ColorProperty.map(eachColor =>
          <Grid item xs={12} sm={12} md={6} lg={6} key={eachColor.key}>
            <Box display={'flex'} alignItems={'center'} position={'relative'} mb={1}>
              <div
                className={classes.swatch}
                style={{ backgroundColor: colors[eachColor.key] }}
                onClick={() => visibleColorPicker(eachColor.key)}
              />
              {colorPicker?.[eachColor.key] && (
                <div className={classes.popover}>
                  <div className={classes.cover} onClick={closeColorPicker} />
                  <SketchPicker color={colors[eachColor.key]} onChange={color => handleColorChange(color.hex, eachColor.key)} />
                </div>
              )}
              <Box ml={1} display={'flex'} flexDirection={'column'} justifyContent={'center'}>
                <Typography variant='body2' className={classes.colorTitle}>{eachColor.value}</Typography>
                <input
                  type='text'
                  value={colors[eachColor.key]}
                  onChange={(e) => handleColorChange(e.target.value, eachColor.key)}
                  disabled={isDisabled()}
                  className={classes.colorInput}
                />
              </Box>
            </Box>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};
